import React, { useState, useMemo, useEffect } from 'react';
import Table from '../Table';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { ButtonPrimary } from '../../Buttons';

const PAGE_SIZE = 50;

const ErrorTable = () => {
  const [errors, setErrors] = useState({});
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { sendAuthenticatedRequest } = useAuth();

  const loadErrors = async ({ initial = false } = {}) => {
    try {
      setIsLoading(true);

      const lastTs =
        initial || nextCursor === null || nextCursor === undefined
          ? -1
          : nextCursor;

      const url = `/errors?as_csv=n&last_timestamp=${lastTs}&page_size=${PAGE_SIZE}`;
      const response = await sendAuthenticatedRequest(url, 'GET');
      if (!response) {
        toast.error('No response from server');
        return;
      }

      const { items = {}, next_cursor = null } = response;

      // Combine old + new errors, then sort descending by unix_time
      setErrors((prev) => {
        const merged = { ...prev, ...items };
        const sortedEntries = Object.entries(merged).sort(
          (a, b) => b[1].unix_time - a[1].unix_time
        );
        return Object.fromEntries(sortedEntries);
      });

      setNextCursor(next_cursor);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load errors');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-load on mount
  useEffect(() => {
    if (Object.keys(errors).length === 0 && !isLoading) {
      loadErrors({ initial: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editError = async (logId, notes) => {
    try {
      const res = await sendAuthenticatedRequest(`/errors/${logId}`, 'PATCH', {
        notes,
      });
      if (res && res.success) {
        setErrors((prev) => {
          const updated = {
            ...prev,
            [logId]: { ...prev[logId], notes },
          };
          const sortedEntries = Object.entries(updated).sort(
            (a, b) => b[1].unix_time - a[1].unix_time
          );
          return Object.fromEntries(sortedEntries);
        });
        toast.success('Error updated');
      } else {
        toast.error('Failed to update error');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to update error');
    }
  };

  const rows = useMemo(() => {
    return Object.entries(errors).map(([logId, error]) => [
      new Date(error.unix_time * 1000).toLocaleString(),
      error.serial,
      error.error_code,
      error.message ?? error.log_description,
      <textarea
        key={`notes-${logId}`}
        defaultValue={error.notes}
        onBlur={(e) => editError(logId, e.target.value)}
        className="w-full p-2 border border-gray-300 rounded"
        rows="1"
      />,
    ]);
  }, [errors]);

  const headers = ['Time', 'Serial Number', 'Error', 'Error Message', 'Notes'];

  const hasMore = nextCursor !== null && nextCursor !== undefined;

  const buttonLabel =
    Object.keys(errors).length === 0
      ? isLoading
        ? 'Loading…'
        : 'Load Errors'
      : isLoading
        ? 'Loading…'
        : hasMore
          ? 'Load More'
          : 'No More';

  return (
    <div className="my-6">
      <h2 className="text-2xl font-semibold mb-4">Error Table</h2>

      <Table
        headers={headers}
        rows={rows}
        searchable={true}
        sortable={true}
        paginated={true}
        initialPageSize={10}
        pageSizeOptions={[10, 25, 50, 'all']}
      />

      <div className="flex flex-row mt-4">
        <ButtonPrimary
          onClick={() =>
            loadErrors({ initial: Object.keys(errors).length === 0 })
          }
          disabled={isLoading || (!hasMore && Object.keys(errors).length > 0)}
        >
          {buttonLabel}
        </ButtonPrimary>
      </div>
    </div>
  );
};

export default ErrorTable;
